import jsPDF from "jspdf";
import brand_logo from "../../../assets/brand_logo.png";
import dayjs from "dayjs";

const loadImageAsBase64 = async (url) => {
  try {
    if (!url) return null;

    const res = await fetch(url);
    if (!res.ok) return null;

    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Image load failed:", url, error);
    return null;
  }
};

const getImageType = (base64 = "") => {
  if (base64.includes("image/jpeg") || base64.includes("image/jpg")) return "JPEG";
  if (base64.includes("image/webp")) return "WEBP";
  return "PNG";
};

const generateBulkPDF = async ({
  bookIds = [],
  rooms = [],
  form = {},
  nights = 0,
  grandTotal = 0,
  paidAmount = 0,
  receivedAdvance = 0,
  remainingPayable = 0,
  discountAmount = 0,
  amountType = null,
  paymentStatus = "",
  roomData = {},
  paidAmounts = {},
  loyaltyDiscountPercent = 0,
  loyaltyDiscountAmount = 0,
  gst = 0,
  gstPercent = 0,
  gstAmount = 0,
  headerImg = "",
  footerImg = "",

  hourlyBook,
  totalHours,
  bookingType,
  ratePerHour,

  overdue_amount = 0,
  overdue_duration = "",
  early_checkin_amount = 0,
  full_book,
}) => {
  const W = 80;
  const margin = 6;
  const contentW = W - margin * 2;
  let y = 0;

  const LOGO_H = 18;
  const LOGO_W = 52;
  const HEADER_TOP_GAP = 5;
  const FOOTER_H = 16;

  const fmtRs = (v, decimals = 0) =>
    `Rs.${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;

  const totalPayable = Number(grandTotal || 0);
  const paid = Number(paidAmount || 0);
  const discount = Number(discountAmount || 0);
  const advance = Number(receivedAdvance || 0);
  const remaining = Number(remainingPayable || 0);

  const isDiscount = amountType === "discount" || discount > 0;
  const isAdvance = amountType === "advance" || advance > 0 || remaining > 0;

  const isFullPaid = isDiscount
    ? paid + discount >= totalPayable
    : remaining <= 0 && paid >= totalPayable;

  const finalStatus =
    paymentStatus ||
    (isDiscount
      ? "Discount Applied"
      : isFullPaid
      ? "Fully Paid"
      : "Advance Received");

  const loyaltyPercent = Number(loyaltyDiscountPercent || 0);
  const loyaltyAmount = Number(loyaltyDiscountAmount || 0);

  const finalGstPercent = Number(gstPercent || gst || 0).toFixed();

  const finalGstAmount =
    finalGstPercent > 0
      ? Number(
          gstAmount ||
            totalPayable - totalPayable / (1 + finalGstPercent / 100)
        )
      : 0;

  const extraHourlyHeight =
    (bookingType ? 10 : 0) +
    (totalHours ? 6 : 0) +
    (ratePerHour ? 6 : 0);

  const paymentRowCount =
    1 +
    (form?.customer_gst ? 1 : 0) +
    (Number(gstPercent || gst) > 0 ? 1 : 0) +
    (Number(loyaltyDiscountPercent) > 0 ? 1 : 0) +
    (Number(early_checkin_amount) > 0 ? 1 : 0) +
    (Number(overdue_amount) > 0 ? 1 : 0) +
    1 +
    (discountAmount > 0 ? 1 : 0) +
    ((amountType === "advance" || receivedAdvance > 0) && discountAmount <= 0
      ? 2
      : 0) +
    1;

  const baseHeight =
    HEADER_TOP_GAP +
    LOGO_H +
    5 +
    4 +
    5 +
    5 +
    5 +
    (form?.address ? 5 : 0) +
    7 +
    5 +
    5 +
    5 +
    5 +
    rooms.length * 5 +
    7 +
    5 +
    paymentRowCount * 5 +
    extraHourlyHeight +
    (footerImg ? FOOTER_H : -20);

  // const doc = new jsPDF({
  //   unit: "mm",
  //   format: [W, Math.max(baseHeight - 40, 120)],
  //   compress: true,
  // });

  // Dynamic page height calculation
const dynamicHeight = Math.max(y + baseHeight + 40, 160);

const doc = new jsPDF({
  unit: "mm",
  format: [W, dynamicHeight],
  compress: true,
});

  const centerText = (text, yPos) => {
    const textW = doc.getTextWidth(text);
    doc.text(text, (W - textW) / 2, yPos);
  };

  const dashedLine = (yPos) => {
    doc.setDrawColor(180);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(margin, yPos, W - margin, yPos);
    doc.setLineDashPattern([], 0);
  };

  const solidLine = (yPos) => {
    doc.setDrawColor(180);
    doc.setLineWidth(0.2);
    doc.line(margin, yPos, W - margin, yPos);
  };

  const row = (label, value, yPos) => {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(String(label), margin, yPos);

    doc.setTextColor(20);
    const valStr = String(value ?? "—");
    const valW = doc.getTextWidth(valStr);
    doc.text(valStr, W - margin - valW, yPos);
  };

  const rowWrap = (label, value, yPos) => {
    const valStr = String(value ?? "—");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");

    const labelW = doc.getTextWidth(label) + 2;
    const availW = contentW - labelW;
    const valW = doc.getTextWidth(valStr);

    if (valW <= availW) {
      doc.setTextColor(0, 0, 0);
      doc.text(label, margin, yPos);

      doc.setTextColor(20);
      doc.text(valStr, W - margin - valW, yPos);
      return yPos + 5;
    }

    doc.setTextColor(0, 0, 0);
    doc.text(label, margin, yPos);
    yPos += 4;

    const lines = doc.splitTextToSize(valStr, contentW);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(20);

    lines.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 4;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);

    return yPos + 1;
  };

  const sectionTitle = (text, yPos) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.8);
    doc.setTextColor(0, 0, 0);
    doc.text(text, margin, yPos);
    solidLine(yPos + 1.5);
    return yPos + 5;
  };

  const logoX = (W - LOGO_W) / 2;
  const headerBase64 = await loadImageAsBase64(headerImg);

  if (headerBase64) {
    doc.addImage(
      headerBase64,
      getImageType(headerBase64),
      0,
      HEADER_TOP_GAP,
      W,
      LOGO_H + 5
    );
  } else {
    try {
      doc.addImage(brand_logo, "PNG", logoX, HEADER_TOP_GAP, LOGO_W, LOGO_H);
    } catch {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      centerText("[Logo]", HEADER_TOP_GAP + LOGO_H / 2);
    }
  }

  y = HEADER_TOP_GAP + LOGO_H + 5 + 4;

  y = sectionTitle("GUEST DETAILS", y);
  y = rowWrap("Guest Name", form?.guest_name, y);
  row("Phone", form?.phone, y);
  y += 5;

  if (form?.email) {
    row("Email", form.email, y);
    y += 5;
  }

  if (form?.id_type) {
    row("ID Type", form.id_type, y);
    y += 5;
  }

  if (form?.id_number) {
    row("ID Number", form.id_number, y);
    y += 5;
  }

  if (form?.address) {
    y = rowWrap("Address", form.address, y);
  }

  y += 2;
  dashedLine(y);
  y += 5;

  y = sectionTitle("STAY DETAILS", y);

  row(
    "Check-In",
    `${form?.check_in || "—"}${
      form?.check_in_time ? ` (${form.check_in_time})` : ""
    }`,
    y
  );
  y += 5;

  row(
    "Check-Out",
    `${form?.check_out || "—"}${
      form?.check_out_time ? ` (${form.check_out_time})` : ""
    }`,
    y
  );
  y += 5;

  if (bookingType) {
    row("Booking Type", bookingType, y);
    y += 5;
  }

  if (totalHours) {
    row("Total Hours", totalHours, y);
    y += 5;
  }

  if (ratePerHour) {
    row("Rate Per Hour", fmtRs(ratePerHour), y);
    y += 5;
  }

  row("Total Guests", form?.guests || "—", y);
  y += 5;

  rooms.forEach((roomItem) => {
    const roomLabel = roomItem?.room_name || "Room";
    const roomNum = roomItem?.room_number ? ` (#${roomItem.room_number})` : "";
    row("Room Details", `${roomLabel}${roomNum}`, y);
    y += 5;
  });

  y += 2;
  dashedLine(y);
  y += 5;

  y = sectionTitle("PAYMENT SUMMARY", y);

  row("Payment Mode", form?.payment_mode || "—", y);
  y += 5;

  if (form?.customer_gst) {
    row("GST Number", form.customer_gst, y);
    y += 5;
  }

  if (finalGstPercent > 0) {
    row(`GST  (${finalGstPercent}%)`, fmtRs(finalGstAmount), y);
    y += 5;
  }

  if (loyaltyPercent > 0) {
    row(`Loyalty Discount (${loyaltyPercent}%)`, `-${fmtRs(loyaltyAmount)}`, y);
    y += 5;
  }

  if (Number(early_checkin_amount) > 0) {
    row("Early Check-in Charge", `+${fmtRs(early_checkin_amount)}`, y);
    y += 5;
  }

  // if (Number(overdue_amount) > 0) {
  //   row(
  //     `Overdue Charges${overdue_duration ? ` (${overdue_duration})` : ""}`,
  //     fmtRs(overdue_amount),
  //     y
  //   );
  //   y += 5;
  // }

  row(
    "Total Payable Amount",
    fmtRs(Number(totalPayable) + Number(overdue_amount || 0) - Number(discount)),
    y
  );
  y += 5;

  if (isDiscount) {
    row("Discount Applied", fmtRs(discount), y);
    y += 5;
  }

  if (isAdvance && !isDiscount) {
    row("Advance Received", fmtRs(advance || paid), y);
    y += 5;

    row("Remaining Amount", fmtRs(remaining), y);
    y += 5;
  }

  // row("Payment Status", finalStatus, y);
  // y += 7;

  doc.setFillColor(240, 255, 244);
  doc.roundedRect(margin, y, contentW, 9, 1.8, 1.8, "F");

  dashedLine(y);
  y += 4;

  const printed = new Date()
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(150);
  centerText(`Printed: ${printed}`, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(0, 0, 0);
  centerText("Thank you for your stay visit again!", y);

  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = pdfUrl;

  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(pdfUrl);
    }, 3000);
  };

  const link = document.createElement("a");
  link.href = pdfUrl;
  link.download = `hotel-room-bill-${dayjs().format("DD-MM-YYYY-hh-mm-A")}.pdf`;
};

export default generateBulkPDF;